/**
 * Text diff — pure line-based diff via Longest Common Subsequence.
 * Deterministic → safe to render server-side.
 */

export type DiffType = "equal" | "add" | "remove";

export interface DiffOp {
  type: DiffType;
  line: string;
}

function toLines(text: string): string[] {
  return text === "" ? [] : text.split("\n");
}

/**
 * Line-by-line diff of `a` (original) → `b` (changed). Removals are emitted
 * before additions at the same position.
 */
export function diffLines(a: string, b: string): DiffOp[] {
  const aLines = toLines(a);
  const bLines = toLines(b);
  const n = aLines.length;
  const m = bLines.length;

  // dp[i][j] = LCS length of aLines[i:] and bLines[j:]
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        aLines[i] === bLines[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aLines[i] === bLines[j]) {
      ops.push({ type: "equal", line: aLines[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "remove", line: aLines[i] });
      i++;
    } else {
      ops.push({ type: "add", line: bLines[j] });
      j++;
    }
  }
  while (i < n) {
    ops.push({ type: "remove", line: aLines[i] });
    i++;
  }
  while (j < m) {
    ops.push({ type: "add", line: bLines[j] });
    j++;
  }
  return ops;
}

export function diffCounts(ops: DiffOp[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const op of ops) {
    if (op.type === "add") added++;
    else if (op.type === "remove") removed++;
  }
  return { added, removed };
}
